/**
 * NavButton — consistent pill-shaped button used for exit/back/home style
 * navigation. Previously these were `text-only` links that didn't read as
 * tappable on an iPad. All now share a subtle white pill with a shadow,
 * bilingual label, and a minimum 48×48 pt touch target.
 *
 * Usage:
 *   <NavButton ar="← إِنْهَاءٌ" en="Exit" onClick={...} />
 *   <NavButton ar="البَيْتُ" en="Home" tone="ghost" onClick={...} />
 */

interface Props {
  ar: string;
  en: string;
  onClick?: () => void;
  /** 'pill' (default): white background + shadow; 'ghost': transparent. */
  tone?: 'pill' | 'ghost';
  /** Extra classes passed to the <button>. */
  class?: string;
  /** Optional right-side accent (emoji, icon). */
  trailing?: string;
  disabled?: boolean;
}

export function NavButton({
  ar,
  en,
  onClick,
  tone = 'pill',
  class: extra,
  trailing,
  disabled,
}: Props) {
  const base =
    'inline-flex flex-col items-center justify-center min-h-[52px] px-4 py-2 rounded-2xl transition-transform active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed';
  const toneClass =
    tone === 'ghost'
      ? 'bg-transparent text-ink/70 hover:bg-ink/5'
      : 'bg-white text-ink shadow-md border border-ink/10';

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      class={`${base} ${toneClass} ${extra ?? ''}`}
    >
      {/* Arabic label uses font-body (Noto Sans Arabic) at 16 px with a
          relaxed line-height — Aref Ruqaa at small sizes was losing
          legibility, especially letters with dots (ت ث ج ش ض). */}
      <span
        class="flex items-center gap-1 font-body font-bold"
        style={{ fontSize: '15px', lineHeight: '1.2' }}
      >
        {trailing && (
          <span class="shrink-0" aria-hidden="true">
            {trailing}
          </span>
        )}
        <span>{ar}</span>
      </span>
      <span
        class="opacity-70 font-body"
        style={{ fontSize: '10px', lineHeight: '1.2', marginTop: '2px' }}
        dir="ltr"
      >
        {en}
      </span>
    </button>
  );
}
